import { toSuggestion } from "./toSuggestion";

import type { SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { GroupKeyFilter } from "@/models/NodeLookupModel";
import type { LibraryItem } from "@corca/data";

/**
 * The types whose typed provider offers a node the document already has, in its "existing"
 * group, so the library does not offer the blank template beside it.
 */
const OFFERED_AS_EXISTING = new Set<string>(["variable", "constant"]);

/**
 * The symbols of those nodes by type. A field editing a node leaves it out through
 * `filterExistingNodes`, as the typed providers do, so an item with its symbol is still offered.
 */
export function existingSymbols(
  doc: DocumentModel,
  filter?: GroupKeyFilter,
): Map<string, Set<string>> {
  const groups = doc.nodeLookup?.groupByKey({
    filter: (base, type, ids) =>
      OFFERED_AS_EXISTING.has(type) && (filter?.(base, type, ids) ?? true),
  });
  const byType = new Map<string, Set<string>>();

  for (const { base, type } of groups?.values() ?? []) {
    const symbols = byType.get(type);

    if (symbols) {
      symbols.add(base);
    } else {
      byType.set(type, new Set([base]));
    }
  }

  return byType;
}

/**
 * The known suggestion for a symbol key, the symbol item before any other carrying it, for the
 * typed providers' questions about a node already in the document, such as the keywords its
 * symbol goes by.
 */
export function librarySuggestionBySymbolKey(
  doc: DocumentModel,
  symbolKey: string,
): SingleSuggestion<"library"> | undefined {
  const item = doc.libraries.itemBySymbolKey(symbolKey);
  return item && toSuggestion(item);
}

/**
 * The known symbol suggestion for a glyph, for the typed providers' new-node path, which names
 * a new node after the symbol typed as its glyph.
 */
export function librarySuggestionByGlyph(
  doc: DocumentModel,
  glyph: string,
): SingleSuggestion<"library"> | undefined {
  const item = doc.libraries.symbolByGlyph(glyph);
  return item && toSuggestion(item);
}

const symbolSuggestionsByItems = new WeakMap<LibraryItem[], SingleSuggestion<"library">[]>();

/**
 * The known symbol suggestions, which the typed providers search to name a new node after the
 * best match for its name, as they searched the symbols file; over every loaded library, since
 * disabling one only hides its rows.
 */
export function librarySymbolSuggestions(doc: DocumentModel): SingleSuggestion<"library">[] {
  const { knownItems } = doc.libraries;
  let symbols = symbolSuggestionsByItems.get(knownItems);

  if (!symbols) {
    symbols = knownItems.filter((item) => item.suggestion?.isSymbol).map(toSuggestion);
    symbolSuggestionsByItems.set(knownItems, symbols);
  }

  return symbols;
}
